import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ClearIcon from "@mui/icons-material/Clear";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import PerformerForm from "../../Admin/Organizations/Performers/PerformerForm";

const denormalizeFromOrg = (orgPerformer, order) => ({
  performerId: orgPerformer.id,
  name: orgPerformer.name ?? "",
  email: orgPerformer.email ?? "",
  links: Array.isArray(orgPerformer.links) ? orgPerformer.links : [],
  bio: orgPerformer.bio ?? "",
  credits: orgPerformer.credits ?? "",
  headshotUrl: orgPerformer.headshotUrl ?? "",
  order,
  isHeadliner: false,
});

const getDisplayName = (p) => (p.name || "").trim() || "Unnamed";

export const AssignPerformerModal = ({
  open,
  onClose,
  organizationId,
  organizationPerformers,
  eventPerformers,
  onAdd,
  onAddNewPerformer,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [addNewOpen, setAddNewOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setAddNewOpen(false);
    }
  }, [open]);
  const assignedIds = (eventPerformers || []).map((p) => p.performerId);
  const available = (organizationPerformers || []).filter(
    (p) => p.id && !assignedIds.includes(p.id),
  );
  const sortedAvailable = [...available].sort((a, b) =>
    getDisplayName(a).localeCompare(getDisplayName(b), undefined, { sensitivity: "base" }),
  );
  const searchLower = (searchQuery || "").trim().toLowerCase();
  const filteredAvailable = searchLower
    ? sortedAvailable.filter((p) => {
        const name = getDisplayName(p).toLowerCase();
        const email = (p.email || "").toLowerCase();
        return name.includes(searchLower) || email.includes(searchLower);
      })
    : sortedAvailable;
  const nextOrder = (eventPerformers || []).length;

  const handleSelect = (orgPerformer) => {
    onAdd(denormalizeFromOrg(orgPerformer, nextOrder));
    onClose();
  };

  const handleAddNewSuccess = (newPerformer) => {
    if (newPerformer?.id && onAddNewPerformer) {
      onAddNewPerformer(newPerformer);
    }
    setAddNewOpen(false);
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Assign performer
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {organizationId && onAddNewPerformer && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddNewOpen(true)}
            sx={{ mb: 2, width: "100%" }}
          >
            Add new
          </Button>
        )}
        {available.length === 0 ? (
          <Typography color="text.secondary">
            All organization performers are already assigned, or there are none.
            Add performers in the organization Performers tab first, or use &quot;Add new&quot; above.
          </Typography>
        ) : (
          <>
            <TextField
              size="small"
              placeholder="Search performers…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear search"
                      onClick={() => setSearchQuery("")}
                      edge="end"
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ mb: 2, width: "100%" }}
            />
            {filteredAvailable.length === 0 ? (
              <Typography color="text.secondary">
                No performers match your search.
              </Typography>
            ) : (
              <List dense sx={{ py: 0 }}>
                {filteredAvailable.map((p) => {
                  const displayName = getDisplayName(p);
              return (
                <ListItem key={p.id} disablePadding sx={{ py: 0.75 }}>
                  <ListItemButton
                    onClick={() => handleSelect(p)}
                    sx={{ gap: 1, alignItems: "center" }}
                  >
                    {p.headshotUrl ? (
                      <Box
                        component="img"
                        src={p.headshotUrl}
                        alt=""
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <PersonIcon color="action" sx={{ fontSize: 40 }} />
                    )}
                    <Box>
                      <Typography variant="body1">{displayName}</Typography>
                      {p.email && (
                        <Typography variant="body2" color="text.secondary">
                          {p.email}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
              </List>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>

    {organizationId && onAddNewPerformer && (
      <Dialog
        open={addNewOpen}
        onClose={() => setAddNewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Add new performer
          <IconButton
            aria-label="close"
            onClick={() => setAddNewOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PerformerForm
            organizationId={organizationId}
            onSuccess={handleAddNewSuccess}
            onCancel={() => setAddNewOpen(false)}
            submitLabel="Add and assign to event"
          />
        </DialogContent>
      </Dialog>
    )}
    </>
  );
};
